import { useParams } from 'react-router';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateBookingSession } from '../api';
import { BookingSession } from '../types';
import {
  buildClientCleanerRestrictionWarningMessage,
  getClientCleanerRestrictionWarnings,
  showClientCleanerRestrictionWarnings,
} from '../utils';
import type { User } from '@/features/users/shared/types';

type PostSessionHookParams = {
  onSuccessFn?: () => void;
  successText?: string;
  warningCleaners?: Pick<User, 'id' | 'first_name' | 'last_name' | 'email'>[];
  onRestrictionWarningMessages?: (messages: string[]) => void;
};

export default function usePostBookingSessionUpdate({
  onSuccessFn,
  successText,
  warningCleaners,
  onRestrictionWarningMessages,
}: PostSessionHookParams) {
  const { errorMsg, successMsg, warningMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();
  const { sessionFilter } = useParams();

  const sessionUpdateMutation = useMutation({
    mutationFn: ({
      bookingSessionId,
      bookingSessionDto,
    }: {
      bookingSessionId: number;
      bookingSessionDto: Partial<BookingSession>;
    }) => updateBookingSession(bookingSessionId, bookingSessionDto),
    onSuccess: (data, variables) => {
      successMsg(successText ?? `Session updated`);
      showClientCleanerRestrictionWarnings(data, warningMsg, warningCleaners);
      const restrictionWarningMessages = getClientCleanerRestrictionWarnings(
        data
      ).map((warning) =>
        buildClientCleanerRestrictionWarningMessage(warning, warningCleaners)
      );
      if (restrictionWarningMessages.length) {
        onRestrictionWarningMessages?.(restrictionWarningMessages);
      }
      if (data) {
        queryClient.setQueryData<BookingSession | undefined>(
          ['bookingSession', String(variables.bookingSessionId)],
          (currentSession) =>
            currentSession ? { ...currentSession, ...data } : data
        );
      }
      queryClient.invalidateQueries([
        'bookingSession',
        String(variables.bookingSessionId),
      ]);
      queryClient.invalidateQueries([
        'bookingSessions',
        sessionFilter,
        String(variables.bookingSessionId) || '',
      ]);
      // SessionTableRow - we need to invalidate the list query
      queryClient.invalidateQueries(['bookingSessions']);
      onSuccessFn && onSuccessFn();
    },
    onError: (error) => {
      errorMsg(error, `Something went wrong while updating session`);
    },
  });

  return { sessionUpdateMutation, errorMsg, successMsg };
}
